import { useCallback, useEffect, useRef, useState } from 'react'
import { serializePath, parsePath } from './routes.js'

// Pure hash <-> route mapping for the app's top-level location. Route shapes
// match Taskboard.jsx. See docs/superpowers/specs/2026-07-14-nav-persistence-and-settings-page-design.md.

function sortedEntries(obj) {
  return Object.keys(obj).sort()
}

// query object -> "?k=v&..." (sorted, empties omitted, encoded). '' if nothing survives.
function serializeQuery(query) {
  if (!query) return ''
  const pairs = sortedEntries(query)
    .filter((k) => { const v = query[k]; return v !== null && v !== undefined && v !== '' && v !== false })
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(query[k])}`)
  return pairs.length ? '?' + pairs.join('&') : ''
}

// "k=v&k2=v2" -> { k: 'v', ... } with alphabetically ordered keys. Last value wins.
function parseQuery(str) {
  const raw = {}
  for (const pair of String(str || '').split('&')) {
    if (!pair) continue
    const eq = pair.indexOf('=')
    const k = decodeURIComponent(eq === -1 ? pair : pair.slice(0, eq))
    const v = eq === -1 ? '' : decodeURIComponent(pair.slice(eq + 1))
    if (k) raw[k] = v
  }
  const out = {}
  for (const k of Object.keys(raw).sort()) out[k] = raw[k]
  return out
}

// Merge `patch` into route.query. null/undefined/''/false delete a key. Pure.
export function mergeQuery(route, patch) {
  const merged = { ...(route.query || {}) }
  for (const k of Object.keys(patch)) {
    const v = patch[k]
    if (v === null || v === undefined || v === '' || v === false) delete merged[k]
    else merged[k] = String(v)
  }
  const next = { ...route }
  delete next.query
  const keys = Object.keys(merged).sort()
  if (keys.length) { next.query = {}; for (const k of keys) next.query[k] = merged[k] }
  return next
}

// Peek panel is a URL query-state (?peek=type:id) so browser Back closes it and
// the link is shareable. Built on mergeQuery — see the record-peek-panel design doc.
export function peekToken(route) {
  return (route && route.query && route.query.peek) || null
}
export function withPeek(route, token) {
  return mergeQuery(route, { peek: token })
}
export function clearPeek(route) {
  return mergeQuery(route, { peek: null })
}

// Query keys that are transient overlays on an index (a task modal, a peek panel),
// not list-state. Dropped when carrying an index's query onto a record URL.
const OVERLAY_QUERY_KEYS = ['task', 'peek']

// The index list-state (filters/search/view) to carry onto a record URL so the
// in-app Back button can restore it — the URL stays the single source of truth.
// Only an *index* route (no recordId) seeds this: opening one record from another
// must not bleed the first index's filters onto an unrelated record type. Returns
// undefined when nothing should be carried (so the route serializes with no query).
export function indexQuery(route) {
  if (!route || route.recordId || !route.query) return undefined
  const keys = Object.keys(route.query).filter((k) => !OVERLAY_QUERY_KEYS.includes(k)).sort()
  if (!keys.length) return undefined
  const out = {}
  for (const k of keys) out[k] = route.query[k]
  return out
}

// route -> hash body (no leading '#'), including the '?...' query part.
export function serializeRoute(route) {
  return serializePath(route) + serializeQuery(route && route.query)
}

// hash string (with/without leading '#','/') -> route object
export function parseHash(hash) {
  const body = String(hash || '').replace(/^#/, '')
  const qIndex = body.indexOf('?')
  const pathRaw = (qIndex === -1 ? body : body.slice(0, qIndex)).replace(/^\/+/, '')
  const queryRaw = qIndex === -1 ? '' : body.slice(qIndex + 1)
  const parts = pathRaw.split('/').filter(Boolean)
  const route = parsePath(parts)
  const query = parseQuery(queryRaw)
  if (Object.keys(query).length) route.query = query
  return route
}

// Binds the route to window.location.hash. `navigate(route)` writes the hash;
// the hashchange listener re-derives `route` (so Back/Forward work too).
export function useHashRoute() {
  const [route, setRouteState] = useState(() =>
    parseHash(typeof window === 'undefined' ? '' : window.location.hash),
  )
  useEffect(() => {
    const onChange = () => setRouteState(parseHash(window.location.hash))
    window.addEventListener('hashchange', onChange)
    onChange() // sync in case the hash changed before this mounted
    return () => window.removeEventListener('hashchange', onChange)
  }, [])
  const navigate = useCallback((next, opts = {}) => {
    const body = serializeRoute(next)
    const target = '#' + body
    const norm = (h) => (h === '' || h === '#') ? '#/' : h
    if (norm(window.location.hash) === norm(target)) {
      setRouteState(parseHash(window.location.hash)) // no hashchange will fire; sync manually
      return
    }
    if (opts.replace) {
      window.history.replaceState(null, '', target)
      setRouteState(parseHash(body))
    } else {
      window.location.hash = target
    }
  }, [])
  return { route, navigate }
}

// Controlled search input backed by a URL query value. Returns [local, onChange]:
// `local` echoes typing immediately; `onChange(v)` debounces commit(v); a change
// to `urlValue` (Back/Forward, route switch) re-syncs `local`.
export function useSearchInput(urlValue, commit, delay = 250) {
  const [local, setLocal] = useState(urlValue || '')
  const commitRef = useRef(commit)
  useEffect(() => { commitRef.current = commit })
  useEffect(() => { setLocal(urlValue || '') }, [urlValue])
  const timer = useRef(null)
  const onChange = useCallback((v) => {
    setLocal(v)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => commitRef.current(v), delay)
  }, [delay])
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])
  return [local, onChange]
}
